import {
  CRS,
  ImageOverlay,
  LatLngBounds,
  type CRS as LeafletCRS,
  type ImageOverlayOptions,
  type LatLngBoundsExpression,
  type Map as LeafletMap,
  type WMSParams,
} from "leaflet";
import {
  createElementObject,
  createLayerComponent,
  withPane,
  type EventedProps,
} from "@react-leaflet/core";
import * as Sentry from "@sentry/react";
import { EMPTY_PIXEL, ZENRIN_BASE_URL } from "@/features/map/constants";
import { isAbortError } from "@/features/map/utils/isAbortError";

export const ZENRIN_BLUE_MAP_MIN_ZOOM = 18;
export const ZENRIN_BLUE_MAP_MAX_ZOOM = 22;
export const ZENRIN_BLUE_MAP_LAYER_NAME = "ブルーマップ";

// -----------------------------------------------------------------------------
// 型定義と基本設定
// -----------------------------------------------------------------------------

type Header = Readonly<{
  header: string;
  value: string;
}>;

const DEFAULT_API_KEY = import.meta.env.VITE_ZENRIN_API_KEY as
  | string
  | undefined;

const DEFAULT_ZENRIN_HOST = (
  (import.meta.env.VITE_ZENRIN_MAPS_API_HOST as string | undefined) ??
  ZENRIN_BASE_URL
).replace(/\/$/, "");

export const DEFAULT_ZENRIN_BLUE_MAP_WMS_URL = `${DEFAULT_ZENRIN_HOST}/map/wms/bluemap`;

const DEFAULT_HEADERS: readonly Header[] = [
  ...(DEFAULT_API_KEY ? [{ header: "x-api-key", value: DEFAULT_API_KEY }] : []),
] as const;

type ExtendedWMSParams = WMSParams & {
  crs?: string;
  [key: string]: string | number | boolean | undefined;
};

const DEFAULT_WMS_PARAMS: ExtendedWMSParams = {
  // layers: "lp1,ll1,lc1,lw1",
  layers: "lc1,lw1",
  format: "image/png",
  version: "1.3.0",
};

// -----------------------------------------------------------------------------
// ユーティリティ関数
// -----------------------------------------------------------------------------

/**
 * React-Leaflet のコンテキストには map か layerContainer が入り得るため、
 * ここで Leaflet.Map の振る舞いを満たしているか検査しておく。
 */
function isLeafletMap(value: unknown): value is LeafletMap {
  return (
    value != null &&
    typeof value === "object" &&
    typeof (value as LeafletMap).getBounds === "function" &&
    typeof (value as LeafletMap).getZoom === "function"
  );
}

interface FetchTileImageResult {
  blob: Blob | null;
  isTokenInvalid: boolean;
}

/**
 * Zenrin API は OAuth トークンが必須なので、
 * 汎用 fetch ではなくヘッダー付きで Blob を取得する専用関数を用意する。
 */
async function fetchTileImage(
  url: string,
  headers: readonly Header[],
  signal?: AbortSignal,
  oauthToken?: string
): Promise<FetchTileImageResult> {
  const headerEntries: HeadersInit = Object.fromEntries(
    headers
      .filter((item) => item.value)
      .map((item) => [item.header, item.value] as const)
  );

  // OAuthトークンが提供されている場合は、Authorizationヘッダーを追加
  if (oauthToken) {
    headerEntries.Authorization = `Bearer ${oauthToken}`;
  }

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: headerEntries,
      mode: "cors",
      signal,
    });

    if (!response.ok) {
      // 403エラーでinvalid_tokenの場合、トークン再取得をトリガー
      if (response.status === 403) {
        const wwwAuthenticate = response.headers.get("Www-Authenticate");
        if (wwwAuthenticate?.includes("invalid_token")) {
          return { blob: null, isTokenInvalid: true };
        }
      }
      const errorMessage = `Zenrin BlueMap HTTP error: ${response.status} ${response.statusText}`;
      console.error(errorMessage);
      Sentry.captureException(new Error(errorMessage));
      return { blob: null, isTokenInvalid: false };
    }

    return { blob: await response.blob(), isTokenInvalid: false };
  } catch (error: unknown) {
    if (signal?.aborted === true || isAbortError(error)) {
      return { blob: null, isTokenInvalid: false };
    }
    console.error("Zenrin BlueMap fetch error", error);
    Sentry.captureException(error);
    return { blob: null, isTokenInvalid: false };
  }
}

/**
 * ブラウザと Leaflet の両方からヘッダーが渡される場合があるため、
 * 共通ヘッダーに対して上書きを行い最終的な送信ヘッダーを確定させる。
 * ヘッダー名の比較は大文字小文字を区別せず、後勝ちでマージされる。
 */
function mergeHeaders(
  baseHeaders: readonly Header[],
  overrideHeaders?: readonly Header[]
): Header[] {
  const merged = new Map<string, Header>();

  baseHeaders.forEach((item) => {
    merged.set(item.header.toLowerCase(), item);
  });

  overrideHeaders?.forEach((item) => {
    merged.set(item.header.toLowerCase(), item);
  });

  return Array.from(merged.values());
}

const UNSUPPORTED_WMS_KEYS = new Set(["service", "transparent", "styles"]);

/**
 * WMS 仕様外のパラメータが紛れ込むと Zenrin 側でエラーとなるため、
 * サポート外キーを事前に除去しておく。
 */
function sanitizeWMSParams(
  params?: ExtendedWMSParams
): ExtendedWMSParams | undefined {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !UNSUPPORTED_WMS_KEYS.has(key.toLowerCase())
    )
  ) as ExtendedWMSParams;
}

interface WMSImageOverlayOptions extends ImageOverlayOptions {
  params?: ExtendedWMSParams;
  crs?: LeafletCRS;
  uppercase?: boolean;
  minZoom?: number;
  maxZoom?: number;
}

/**
 * Leaflet.ImageOverlay を拡張し、Zenrin WMS を CORS 付きで取得するための
 * 認証ヘッダー管理と再描画ロジックを追加したクラス。
 * 地図操作に追従し、React 側からのパラメータ更新にも対応する。
 */
class WMSImageOverlayWithHeaders extends ImageOverlay {
  private headers: Header[];
  private baseUrl: string;
  private wmsParams: ExtendedWMSParams;
  private fetchId = 0;
  private abortController?: AbortController;
  private updateTimer?: ReturnType<typeof globalThis.setTimeout>;
  private oauthToken?: string;
  private onTokenInvalid?: () => void;

  constructor(
    url: string,
    bounds: LatLngBoundsExpression,
    options: WMSImageOverlayOptions,
    headers: readonly Header[],
    oauthToken?: string,
    onTokenInvalid?: () => void
  ) {
    // Leaflet.ImageOverlay はコンストラクタで URL を必要とするため、仮画像を渡し後から実データに差し替える。
    super(EMPTY_PIXEL, bounds, options);
    this.headers = [...headers];
    this.baseUrl = url;
    this.oauthToken = oauthToken;
    this.onTokenInvalid = onTokenInvalid;
    this.wmsParams = {
      ...DEFAULT_WMS_PARAMS,
      ...(sanitizeWMSParams(options.params) ?? {}),
    };
  }

  onAdd(map: LeafletMap): this {
    // レイヤーが地図に追加されたタイミングで最新画像を取得し、地図操作イベントを監視する。
    super.onAdd(map);
    this.scheduleImageUpdate();
    map.on("moveend", this.handleMapMoveOrZoom, this);
    map.on("zoomend", this.handleMapMoveOrZoom, this);
    return this;
  }

  onRemove(map: LeafletMap): this {
    // イベントリスナーと進行中のリクエストを確実に解除し、リソースリークを防ぐ。
    map.off("moveend", this.handleMapMoveOrZoom, this);
    map.off("zoomend", this.handleMapMoveOrZoom, this);
    this.abortController?.abort();
    this.abortController = undefined;
    if (this.updateTimer != null) {
      clearTimeout(this.updateTimer);
      this.updateTimer = undefined;
    }
    return super.onRemove(map);
  }

  setParams(params: ExtendedWMSParams | undefined): void {
    this.wmsParams = {
      ...DEFAULT_WMS_PARAMS,
      ...(sanitizeWMSParams(params) ?? {}),
    };
    // React 側からパラメータが変わった場合は即座に再描画させる。
    this.scheduleImageUpdate();
  }

  setRequestMetadata(headers: readonly Header[]): void {
    this.headers = [...headers];
    // 認証情報が変わった場合も最新の組み合わせで再取得する。
    this.scheduleImageUpdate();
  }

  setOAuthToken(token: string | undefined): void {
    this.oauthToken = token;
    // OAuthトークンが変わった場合も最新の組み合わせで再取得する。
    this.scheduleImageUpdate();
  }

  setOnTokenInvalid(callback: (() => void) | undefined): void {
    this.onTokenInvalid = callback;
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
    // WMS エンドポイントが変わったら古いキャッシュを使わず再取得する。
    this.scheduleImageUpdate();
  }

  /** React Leaflet から明示的に再取得させたいとき用のフック。 */
  refresh(): void {
    this.scheduleImageUpdate();
  }

  private readonly handleMapMoveOrZoom = (): void => {
    // moveend / zoomend のどちらからでも同じ更新処理を呼び出す。
    this.scheduleImageUpdate();
  };

  /**
   * Leaflet の move/zoom イベントは頻繁に発火するため、
   * 直近のイベントのみを処理するようタイマーでデバウンスする。
   */
  private scheduleImageUpdate(): void {
    if (this.updateTimer != null) {
      clearTimeout(this.updateTimer);
    }
    this.updateTimer = setTimeout(() => {
      this.updateTimer = undefined;
      this.updateImage();
    }, 0);
  }

  /**
   * Leaflet から受け取った現在の地図状態を WMS パラメータへ変換し、
   * Zenrin から画像を取得して <img> のソースに反映する中心ロジック。
   */
  private updateImage(): void {
    const map = this._map;
    if (!map) {
      return;
    }

    const zoom = map.getZoom();
    const { minZoom, maxZoom } = this.options as WMSImageOverlayOptions;
    const imageElement = this.getElement() as HTMLImageElement | null;

    if (
      (minZoom != null && zoom < minZoom) ||
      (maxZoom != null && zoom > maxZoom)
    ) {
      if (imageElement) {
        imageElement.style.display = "none";
        imageElement.src = EMPTY_PIXEL;
      }
      return;
    }

    if (imageElement) {
      imageElement.style.display = "";
    }

    const bounds = map.getBounds();
    const size = map.getSize();
    // mapContainerSize.ts で一時拡大された印刷・エクスポート用のコンテナであるか判定
    const isPrinting = map.getContainer().hasAttribute("data-printing");
    const dpr =
      isPrinting && typeof window !== "undefined"
        ? window.devicePixelRatio || 1
        : 1;
    const crs = (this.options as WMSImageOverlayOptions).crs ?? CRS.EPSG3857;
    const upperCase =
      (this.options as WMSImageOverlayOptions).uppercase ?? true;

    const projectedSouthWest = crs.project(bounds.getSouthWest());
    const projectedNorthEast = crs.project(bounds.getNorthEast());

    // ゼンリンブルーマップAPIの制限（最大縦横4000px）を考慮し、アスペクト比を維持して制限内に収める
    const MAX_WMS_SIZE = 4000;
    // 事前に最低でも 1 以上の値であることを保証し、ゼロ除算や NaN の発生を防ぐ
    let reqWidth = Math.max(Math.round(size.x * dpr), 1);
    let reqHeight = Math.max(Math.round(size.y * dpr), 1);
    if (reqWidth > MAX_WMS_SIZE || reqHeight > MAX_WMS_SIZE) {
      const scale = Math.min(MAX_WMS_SIZE / reqWidth, MAX_WMS_SIZE / reqHeight);
      reqWidth = Math.round(reqWidth * scale);
      reqHeight = Math.round(reqHeight * scale);
    }

    const params: ExtendedWMSParams = {
      request: "GetMap",
      ...DEFAULT_WMS_PARAMS,
      ...this.wmsParams,
      width: reqWidth,
      height: reqHeight,
      bbox: `${projectedSouthWest.x},${projectedSouthWest.y},${projectedNorthEast.x},${projectedNorthEast.y}`,
      crs:
        this.wmsParams.crs ??
        (crs as unknown as { code?: string }).code ??
        "EPSG:3857",
    };

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value == null) {
        return;
      }
      if (UNSUPPORTED_WMS_KEYS.has(key.toLowerCase())) {
        return;
      }
      searchParams.append(upperCase ? key.toUpperCase() : key, String(value));
    });

    const requestUrl = `${this.baseUrl}?${searchParams.toString()}`;
    this.abortController?.abort();
    const requestId = ++this.fetchId;
    const controller = new AbortController();
    this.abortController = controller;

    void fetchTileImage(
      requestUrl,
      this.headers,
      controller.signal,
      this.oauthToken
    ).then((result) => {
      if (requestId !== this.fetchId) {
        return;
      }

      // トークンが無効な場合、コールバックを呼び出して再取得をトリガー
      if (result.isTokenInvalid) {
        this.onTokenInvalid?.();
        return;
      }

      if (result.blob == null) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (requestId !== this.fetchId) {
          return;
        }
        if (typeof reader.result === "string") {
          this.setBounds(bounds);
          this.setUrl(reader.result);
        } else {
          Sentry.captureException(new Error("Unexpected WMS image payload"));
        }
      };
      reader.onerror = () => {
        Sentry.captureException(
          new Error("Failed to read Zenrin BlueMap image payload")
        );
      };
      reader.readAsDataURL(result.blob);
    });
  }
}

export interface ZenrinBlueMapTileLayerProps
  extends Omit<ImageOverlayOptions, "bounds">,
    EventedProps {
  params?: ExtendedWMSParams;
  headers?: readonly Header[];
  url?: string;
  crs?: LeafletCRS;
  uppercase?: boolean;
  minZoom?: number;
  maxZoom?: number;
  oauthToken?: string;
  onTokenInvalid?: () => void;
}

// -----------------------------------------------------------------------------
// React Leaflet ブリッジ
// -----------------------------------------------------------------------------

/** Zenrin WMS オーバーレイを構成する React Leaflet 向けラッパー。 */
export const ZenrinBlueMapTileLayer = createLayerComponent<
  WMSImageOverlayWithHeaders,
  ZenrinBlueMapTileLayerProps
>(
  // ファクトリ: WMS オーバーレイを生成し初期状態を整える。
  function createZenrinBlueMapTileLayer(
    {
      eventHandlers: _eh,
      params,
      url = DEFAULT_ZENRIN_BLUE_MAP_WMS_URL,
      headers,
      oauthToken,
      onTokenInvalid,
      ...options
    },
    context
  ) {
    const mergedHeaders = mergeHeaders(DEFAULT_HEADERS, headers);
    const optionsWithPane = withPane(
      options,
      context
    ) as WMSImageOverlayOptions;
    const map =
      context.map ??
      (isLeafletMap(context.layerContainer)
        ? context.layerContainer
        : undefined);
    const initialBounds: LatLngBoundsExpression =
      map?.getBounds() ?? new LatLngBounds([0, 0], [0, 0]);

    const layer = new WMSImageOverlayWithHeaders(
      url,
      initialBounds,
      {
        ...optionsWithPane,
        params,
        crs: optionsWithPane.crs ?? CRS.EPSG3857,
        uppercase: optionsWithPane.uppercase ?? true,
        className: "zenrin-bluemap-tile-layer", // ブルーマップレイヤーであることを判断できるようにするために名前をつける
      },
      mergedHeaders,
      oauthToken,
      onTokenInvalid
    );

    return createElementObject(layer, context);
  },
  // アップデータ: props の変化に応じて Leaflet レイヤーを更新する。
  function updateZenrinBlueMapTileLayer(layer, props, prevProps) {
    if (props.params != null && props.params !== prevProps.params) {
      layer.setParams(props.params);
    }
    if (props.params == null && prevProps.params != null) {
      layer.setParams(undefined);
    }
    if (props.url != null && props.url !== prevProps.url) {
      layer.setBaseUrl(props.url);
    }
    if (props.headers !== prevProps.headers) {
      const mergedHeaders = mergeHeaders(DEFAULT_HEADERS, props.headers);
      layer.setRequestMetadata(mergedHeaders);
    }
    if (props.oauthToken !== prevProps.oauthToken) {
      layer.setOAuthToken(props.oauthToken);
    }
    if (props.onTokenInvalid !== prevProps.onTokenInvalid) {
      layer.setOnTokenInvalid(props.onTokenInvalid);
    }
    const layerOptions = layer.options as WMSImageOverlayOptions;
    if (props.crs !== prevProps.crs) {
      layerOptions.crs = props.crs ?? CRS.EPSG3857;
      layer.refresh();
    }
    if (props.uppercase !== prevProps.uppercase) {
      layerOptions.uppercase = props.uppercase ?? true;
      layer.refresh();
    }
    if (props.minZoom !== prevProps.minZoom) {
      layerOptions.minZoom = props.minZoom;
      layer.refresh();
    }
    if (props.maxZoom !== prevProps.maxZoom) {
      layerOptions.maxZoom = props.maxZoom;
      layer.refresh();
    }
    if (props.opacity != null && props.opacity !== prevProps.opacity) {
      layer.setOpacity(props.opacity);
    }
    if (props.zIndex != null && props.zIndex !== prevProps.zIndex) {
      layer.setZIndex(props.zIndex);
    }
  }
);
